import React from 'react'
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Fade,
  Zoom,
  alpha,
  useTheme,
} from '@mui/material'
import { ArrowForward, Launch, Visibility } from '@mui/icons-material'
import Layout from '../components/Layout'
import { useTranslation } from 'react-i18next'
import { referenzen } from '../data/referenzen'
import { useColorMode } from '../theme'

interface PageProps {}

const Referenzen: React.FC<PageProps> = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mode } = useColorMode()
  const isDark = mode === 'dark'

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: { xs: '300px', md: '250px' },
            background: isDark
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            zIndex: 0,
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}
        >
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip
                icon={<Visibility />}
                label={t('referencesBadge')}
                sx={{
                  mb: 4,
                  bgcolor: isDark
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 3,
                  py: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                fontWeight={900}
                sx={{
                  mb: 4,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                  lineHeight: { xs: 1.1, sm: 1.2 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                {t('referencesTitle')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                  maxWidth: 600,
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  px: { xs: 2, sm: 1 },
                }}
              >
                {t('referencesSubtitle')}
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {referenzen.map((ref, index) => (
              <Grid key={ref.slug} item xs={12} md={6}>
                <Zoom in timeout={1000 + index * 200}>
                  <Card
                    component="article"
                    sx={{
                      height: '100%',
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha('#2d2d2d', 0.8)} 0%, ${alpha('#3d3d3d', 0.8)} 100%)`
                        : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9ff', 0.9)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderRadius: 4,
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 3, sm: 4 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ mb: 3 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                              color: isDark ? theme.palette.common.white : theme.palette.primary.main,
                              fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            }}
                          >
                            {ref.name}
                          </Typography>
                          <Chip
                            label={ref.industry}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                          />
                        </Stack>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                          lineHeight: 1.6,
                          mb: 3,
                          flexGrow: 1,
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                        }}
                      >
                        {ref.summary}
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{
                            color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                            mb: 1.5,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          }}
                        >
                          {t('referenceTech')}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                          {ref.tech.slice(0, 4).map((tech) => (
                            <Chip
                              key={tech}
                              label={tech}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.main,
                                fontWeight: 500,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{ mt: 'auto' }}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Button
                          href={`/referenzen/${ref.slug}`}
                          variant="contained"
                          endIcon={<ArrowForward />}
                          sx={{ minWidth: 200 }}
                        >
                          {t('referenceDetails')}
                        </Button>
                        {ref.url && (
                          <Button
                            variant="text"
                            color={isDark ? 'secondary' : 'primary'}
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<Launch />}
                            sx={{ fontWeight: 600 }}
                          >
                            {t('referenceVisit')}
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Layout>
  )
}

export default Referenzen
